"use client";

import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { useWorkspaceTabRailActivePosition } from "@/components/workspaces/workspace-tab-rail-position-context";
import { cn } from "@/lib/utils";

export interface WorkspaceContentContainerProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Tab rail rendered directly above the workspace region.
   */
  tabBar?: ReactNode;

  /**
   * Additional classes for the tab rail wrapper.
   */
  tabBarClassName?: string;

  /**
   * Classes applied to the workspace region wrapper (the element that wraps the children).
   */
  workspaceClassName?: string;

  /**
   * Classes applied to the inner workspace surface (the element that directly wraps
   * the provided children inside the padded region).
   */
  innerClassName?: string;

  /**
   * Optional override for the inner workspace background treatment. Defaults to the
   * base surface token fill used across workspace canvases.
   */
  innerBackgroundClassName?: string;

  children: ReactNode;
}

export function WorkspaceContentContainer({
  tabBar,
  tabBarClassName,
  workspaceClassName,
  innerClassName,
  innerBackgroundClassName = "bg-[var(--token-foundation-surface-base)]",
  className,
  children,
  ...rest
}: WorkspaceContentContainerProps) {
  const showTabBar = Boolean(tabBar);
  const activePosition = useWorkspaceTabRailActivePosition();

  const innerCornerClass = (() => {
    if (!showTabBar) {
      return "rounded-[var(--radius-giga)]";
    }
    switch (activePosition) {
      case "first":
        return "rounded-[var(--radius-giga)] rounded-tl-none";
      case "last":
        return "rounded-[var(--radius-giga)] rounded-tr-none";
      case "only":
        return "rounded-bl-[var(--radius-giga)] rounded-br-[var(--radius-giga)] rounded-tl-none rounded-tr-none";
      case "middle":
      case null:
      default:
        return "rounded-[var(--radius-giga)]";
    }
  })();

  return (
    <div
      {...rest}
      className={cn(
        "workspace-content-container box-border flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col gap-0",
        className,
      )}
      data-has-tabbar={showTabBar || undefined}
      data-tab-active-position={showTabBar ? activePosition ?? undefined : undefined}
    >
      {showTabBar ? (
        <div
          className={cn(
            "workspace-rail box-border w-full min-w-0 max-w-full flex-none",
            tabBarClassName,
          )}
        >
          {tabBar}
        </div>
      ) : null}

      <div
        className={cn(
          "workspace-content-region box-border flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col gap-[var(--space-sm)]",
          workspaceClassName,
        )}
      >
        <div
          className={cn(
            "workspace-content-region-inner box-border flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col p-[var(--space-sm)]",
            innerCornerClass,
            innerBackgroundClassName,
            innerClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default WorkspaceContentContainer;
